/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { ArrowLeft, Guitar, Brain, Eye, Target, Lightbulb, Mail, Youtube } from 'lucide-react';

const AboutPage: React.FC = () => {
  // Update page title and meta description for SEO, and scroll to top
  useEffect(() => {
    window.scrollTo(0, 0);

    document.title = 'About Guituitive - Interactive Guitar Fretboard Visualization Tool';

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Learn how Guituitive helps guitarists master the fretboard through interactive visualization, music theory insights, and pattern recognition. Perfect for beginners to advanced players.'
      );
    }
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Header with Back Button */}
      <header className="border-b sticky top-0 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between max-w-6xl">
          <Link to="/">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to App
            </Button>
          </Link>
          <h1 className="text-xl font-bold">About Guituitive</h1>
          <div className="w-24"></div> {/* Spacer for centering */}
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Hero Section */}
        <section className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            Master Your Guitar Fretboard
          </h2>
          <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
            Guituitive is an interactive fretboard visualization tool that helps guitarists
            understand music theory, discover scale patterns, and navigate the entire fretboard
            with confidence.
          </p>
          <Link to="/">
            <Button size="lg" className="font-semibold">
              Try Guituitive Free
            </Button>
          </Link>
        </section>

        {/* What is Guituitive */}
        <section className="mb-16">
          <h3 className="text-3xl font-bold mb-6">What is Guituitive?</h3>
          <div className="prose prose-lg dark:prose-invert max-w-none">
            <p className="text-muted-foreground leading-relaxed">
              Guituitive transforms how guitarists learn and understand their instrument. Instead of
              memorizing countless chord shapes and scale patterns, Guituitive shows you the
              <strong> relationships between notes, intervals, and patterns</strong> across the
              entire fretboard.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-4">
              Whether you're a beginner trying to understand basic scales or an advanced player
              exploring exotic modes and chord voicings, Guituitive provides the visual clarity
              you need to truly <em>understand</em> what you're playing—not just where to put your fingers.
            </p>
          </div>
        </section>

        {/* Key Features */}
        <section className="mb-16">
          <h3 className="text-3xl font-bold mb-8">Key Features</h3>
          <div className="grid md:grid-cols-2 gap-6">
            {/* Feature 1 */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="h-5 w-5 text-blue-500" />
                  Visual Pattern Recognition
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Color-coded interval visualization makes it easy to see how scales and chords
                  repeat across the fretboard. Learn patterns, not positions.
                </p>
              </CardContent>
            </Card>

            {/* Feature 2 */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Brain className="h-5 w-5 text-purple-500" />
                  Music Theory Integration
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Explore scales, chords, modes, and their relationships. See diatonic chords,
                  interval structures, and chord progressions in any key.
                </p>
              </CardContent>
            </Card>

            {/* Feature 3 */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Guitar className="h-5 w-5 text-green-500" />
                  Multiple Instruments & Tunings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Supports 4-8 string instruments with custom tunings. Perfect for guitar, bass,
                  ukulele, and more. Explore different voicings and positions.
                </p>
              </CardContent>
            </Card>

            {/* Feature 4 */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-orange-500" />
                  Interactive Learning
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Jam along with backing tracks, visualize chord fingerings, and use the
                  guitar-powered chat assistant to answer your music theory questions.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* How It Works */}
        <section className="mb-16">
          <h3 className="text-3xl font-bold mb-6">How It Works</h3>
          <Card>
            <CardContent className="pt-6">
              <ol className="space-y-4">
                <li className="flex gap-4">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold">
                    1
                  </span>
                  <div>
                    <h4 className="font-semibold mb-1">Choose Your Key & Scale</h4>
                    <p className="text-sm text-muted-foreground">
                      Select from dozens of scales and modes in any key. From major and minor to
                      exotic modes and jazz scales.
                    </p>
                  </div>
                </li>
                <li className="flex gap-4">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold">
                    2
                  </span>
                  <div>
                    <h4 className="font-semibold mb-1">See the Patterns</h4>
                    <p className="text-sm text-muted-foreground">
                      Each interval is color-coded on the fretboard. Watch how the same pattern
                      repeats in different positions and across different strings.
                    </p>
                  </div>
                </li>
                <li className="flex gap-4">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold">
                    3
                  </span>
                  <div>
                    <h4 className="font-semibold mb-1">Explore & Practice</h4>
                    <p className="text-sm text-muted-foreground">
                      Use jam tracks to practice, explore chord voicings, or animate scale patterns
                      to learn them one note at a time.
                    </p>
                  </div>
                </li>
                <li className="flex gap-4">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold">
                    4
                  </span>
                  <div>
                    <h4 className="font-semibold mb-1">Understand & Apply</h4>
                    <p className="text-sm text-muted-foreground">
                      Connect theory to practice. See which chords belong to a scale, understand
                      interval relationships, and develop true fretboard mastery.
                    </p>
                  </div>
                </li>
              </ol>
            </CardContent>
          </Card>
        </section>

        {/* Who It's For */}
        <section className="mb-16">
          <h3 className="text-3xl font-bold mb-6">Who Is Guituitive For?</h3>
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="text-center">
              <CardContent className="pt-6">
                <Lightbulb className="h-12 w-12 mx-auto mb-4 text-yellow-500" />
                <h4 className="font-semibold mb-2">Beginners</h4>
                <p className="text-sm text-muted-foreground">
                  Build a solid foundation in music theory and fretboard knowledge from day one.
                </p>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardContent className="pt-6">
                <Target className="h-12 w-12 mx-auto mb-4 text-blue-500" />
                <h4 className="font-semibold mb-2">Intermediate Players</h4>
                <p className="text-sm text-muted-foreground">
                  Break out of the box patterns and discover new positions across the fretboard.
                </p>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardContent className="pt-6">
                <Brain className="h-12 w-12 mx-auto mb-4 text-purple-500" />
                <h4 className="font-semibold mb-2">Advanced Musicians</h4>
                <p className="text-sm text-muted-foreground">
                  Explore advanced theory, exotic scales, and complex chord voicings visually.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        <Separator className="my-16" />

        {/* For YouTube Creators */}
        <section className="mb-16">
          <Card className="border-2 border-purple-500/30 bg-gradient-to-br from-purple-500/5 to-blue-500/5">
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="flex-shrink-0">
                  <Youtube className="h-16 w-16 text-red-500" />
                </div>
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-2xl font-bold mb-2">Are You a YouTube Guitar Instructor?</h3>
                  <p className="text-muted-foreground mb-4">
                    Learn how Guituitive can help students discover your lessons through RAG technology—while
                    you retain full credit and control of your content.
                  </p>
                  <Link to="/creators">
                    <Button variant="default" className="bg-purple-600 hover:bg-purple-700">
                      Learn About Our Creator Program
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        <Separator className="my-16" />

        {/* Contact Section */}
        <section className="mb-16">
          <h3 className="text-3xl font-bold mb-8 text-center">Get In Touch</h3>
          <div className="grid md:grid-cols-3 gap-4 max-w-2xl mx-auto">
            <a
              href="mailto:hello@guituitive.com"
              className="flex flex-col items-center gap-2 p-4 rounded-lg border border-border hover:bg-accent hover:border-accent-foreground/20 transition-colors group"
            >
              <Mail className="h-6 w-6 text-blue-500" />
              <div className="text-center">
                <div className="font-medium text-sm">General Inquiries</div>
                <div className="text-xs text-muted-foreground">hello@guituitive.com</div>
              </div>
            </a>
            <a
              href="mailto:support@guituitive.com"
              className="flex flex-col items-center gap-2 p-4 rounded-lg border border-border hover:bg-accent hover:border-accent-foreground/20 transition-colors group"
            >
              <Mail className="h-6 w-6 text-green-500" />
              <div className="text-center">
                <div className="font-medium text-sm">Support</div>
                <div className="text-xs text-muted-foreground">support@guituitive.com</div>
              </div>
            </a>
            <a
              href="mailto:creators@guituitive.com"
              className="flex flex-col items-center gap-2 p-4 rounded-lg border border-border hover:bg-accent hover:border-accent-foreground/20 transition-colors group"
            >
              <Mail className="h-6 w-6 text-purple-500" />
              <div className="text-center">
                <div className="font-medium text-sm">Creators</div>
                <div className="text-xs text-muted-foreground">creators@guituitive.com</div>
              </div>
            </a>
          </div>
        </section>

        {/* CTA */}
        <section className="text-center py-12 px-6 bg-gradient-to-r from-blue-600/10 to-purple-600/10 rounded-lg border border-border">
          <h3 className="text-2xl font-bold mb-4">Ready to Master Your Fretboard?</h3>
          <p className="text-muted-foreground mb-6">
            Start exploring scales, chords, and patterns today—completely free.
          </p>
          <Link to="/">
            <Button size="lg" className="font-semibold">
              Launch Guituitive
            </Button>
          </Link>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t mt-20">
        <div className="container mx-auto px-4 py-8 text-center text-sm text-muted-foreground">
          <p>© 2024-2026 Entangled Harmony Labs, LLC</p>
          <p className="mt-1">All Rights Reserved</p>
        </div>
      </footer>

      {/* Structured Data for SEO */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "Guituitive",
          "applicationCategory": "EducationalApplication",
          "description": "Interactive guitar fretboard visualization tool for learning music theory, scales, chords, and patterns.",
          "url": "https://guituitive.com",
          "author": {
            "@type": "Organization",
            "name": "Entangled Harmony Labs, LLC"
          },
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          },
          "operatingSystem": "Web Browser",
          "keywords": "guitar fretboard, music theory, guitar scales, guitar chords, fretboard visualization, learn guitar",
          "featureList": [
            "Interactive fretboard visualization",
            "Color-coded interval display",
            "Multiple scales and modes",
            "Chord fingering explorer",
            "Backing track integration",
            "Custom tuning support"
          ]
        })}
      </script>
    </div>
  );
};

export default AboutPage;
